/**
 * Vermarktungs-Cockpit — Übersicht + Detailseite
 * ================================================
 * - Übersicht: alle aktiven Inserate + Bewerber-Cards pro Einheit
 * - Detailseite: 6 Tabs pro Bewerber
 *   1. Bewerbung (Antragsdaten Stufe 1)
 *   2. Kommunikation (Nachrichten)
 *   3. Besichtigungen (Termine)
 *   4. Erweiterte Angaben (Stufe 2)
 *   5. Unterlagen (Stufe 3)
 *   6. Aktivitäten (Timeline)
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { MarketingSettingsDialog, NewListingDialog, publicUrl } from "@/components/marketing/ListingEditor";
import { ProspectThread } from "@/components/marketing/ListingWorkspace";
import { ListingTiles } from "@/pages/InseratDetail";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Storefront, User, ChatCircleDots, CalendarBlank, Files, ClipboardText, ClockCounterClockwise,
  ArrowRight, ArrowLeft, Envelope, Phone as PhoneIcon, Warning, CheckCircle,
  Confetti, EnvelopeSimple, Shield, XCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatDate, formatEUR } from "@/lib/constants";

const STATUS_LABELS = {
  new: "Neu",
  in_review: "In Prüfung",
  shortlist: "Kurzliste",
  viewing_scheduled: "Besichtigung geplant",
  offer_sent: "Angebot versendet",
  contract_signed: "Vertrag unterzeichnet",
  rejected: "Abgelehnt",
  withdrawn: "Zurückgezogen",
  reviewing: "In Prüfung",
  viewing_offered: "Zur Besichtigung eingeladen",
  viewing_done: "Besichtigt",
  stage2_requested: "Angaben angefordert",
  stage3_requested: "Unterlagen angefordert",
  preferred: "Für Neuvermietung ausgewählt",
};
const STATUS_CLS = {
  new: "bg-blue-100 text-blue-800 border-blue-200",
  in_review: "bg-amber-100 text-amber-800 border-amber-200",
  shortlist: "bg-emerald-100 text-emerald-800 border-emerald-200",
  viewing_scheduled: "bg-violet-100 text-violet-800 border-violet-200",
  offer_sent: "bg-purple-100 text-purple-800 border-purple-200",
  contract_signed: "bg-green-100 text-green-800 border-green-200",
  rejected: "bg-rose-100 text-rose-800 border-rose-200",
  withdrawn: "bg-slate-100 text-slate-700 border-slate-200",
};
const STAGE_LABELS = { stage1: "Stufe 1", stage2: "Stufe 2", stage3: "Stufe 3", closed: "Abgeschlossen" };

const INCOME_LABELS = {
  employment: "Angestellte(r)", self_employment: "Selbstständig", pension: "Rente",
  public_benefits: "Öffentliche Leistungen", alimony_other: "Unterhalt/Sonstiges",
  multiple: "Mehrere Quellen", other: "Andere",
};
const EMPL_LABELS = { permanent: "Unbefristet", temporary: "Befristet", probation: "Probezeit", training: "Ausbildung/Studium", other: "Andere" };
const BRACKET_LABELS = {
  u1500: "< 1.500 €", "1500_2000": "1.500–2.000 €", "2000_2500": "2.000–2.500 €",
  "2500_3000": "2.500–3.000 €", "3000_4000": "3.000–4.000 €", "4000_5000": "4.000–5.000 €", o5000: "> 5.000 €",
};

// ============================================================================
// Übersicht
// ============================================================================
export default function Vermarktung() {
  const [meta, setMeta] = useState(null);
  const [dlg, setDlg] = useState({});
  const [tilesKey] = useState(0);
  const navigate = useNavigate();
  const loadMeta = () => api.get("/marketing/meta").then(({ data }) => setMeta(data)).catch(() => {});
  useEffect(() => { loadMeta(); }, []);
  const [listings, setListings] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: ls }, { data: ps }] = await Promise.all([
        api.get("/public-listings"),
        api.get("/prospects"),
      ]);
      setListings(ls || []);
      setProspects(ps || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Laden");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const byListing = useMemo(() => {
    const map = {};
    prospects.forEach((p) => {
      const k = p.public_listing_id || "__none__";
      if (!map[k]) map[k] = [];
      map[k].push(p);
    });
    return map;
  }, [prospects]);

  const activeListings = listings.filter((l) => l.status === "active");
  const otherListings = listings.filter((l) => l.status !== "active");

  return (
    <div className="space-y-5" data-testid="vermarktung-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <Storefront size={24} className="text-emerald-700" />
            Vermarktung
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">Alle Inserate auf einen Blick – Klick auf eine Kachel öffnet Übersicht, Bewerbungen, Besichtigungen und Bearbeitung.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setDlg({ settings: true })} data-testid="mk-settings-btn">Einstellungen & Einbettung</Button>
          {meta?.tenant_slug && <a href={publicUrl(meta.tenant_slug)} target="_blank" rel="noreferrer"><Button variant="outline" size="sm" data-testid="mk-public-btn">Öffentliche Seite</Button></a>}
          <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800" onClick={() => setDlg({ create: true })} data-testid="mk-new-btn">Neue Vermarktung</Button>
        </div>
        <div className="text-xs text-slate-500 hidden">
          <b>{activeListings.length}</b> aktive Inserate · <b>{prospects.filter((p) => p.status !== "rejected" && p.status !== "withdrawn").length}</b> offene Bewerbungen
        </div>
      </div>

      <ListingTiles meta={meta} onNew={() => setDlg({ create: true })} reloadKey={tilesKey} />
      <MarketingSettingsDialog open={!!dlg.settings} onOpenChange={() => setDlg({})} meta={meta} onSaved={loadMeta} />
      <NewListingDialog open={!!dlg.create} onOpenChange={() => setDlg({})} meta={meta} onCreated={(l) => navigate(`/vermarktung/inserat/${l.id}?tab=edit`)} />
    </div>
  );
}

function ListingCard({ listing, prospects, onReload, muted, meta, setDlg }) {
  const openProspects = prospects.filter((p) => p.status !== "rejected" && p.status !== "withdrawn");
  return (
    <div
      className={`rounded-lg border overflow-hidden bg-white ${muted ? "opacity-60 border-slate-200" : "border-slate-200 shadow-sm"}`}
      data-testid={`vermarktung-listing-${listing.id}`}
    >
      <div className="px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white flex items-start justify-between gap-3">
        <div>
          <div className="font-semibold text-slate-900">
            {listing.aktenzeichen && <span className="font-mono text-xs text-emerald-800 mr-2" data-testid={`listing-az-${listing.id}`}>{listing.aktenzeichen}</span>}
            {listing.title || "Wohnung"}
            {listing.marketing_type && <span className="ml-2 text-[10px] text-slate-500">{(meta?.types || []).find((t) => t.key === listing.marketing_type)?.label}</span>}
            <Badge variant="outline" className="ml-2 text-[10px]">{{ draft: "Entwurf", active: "Veröffentlicht", reserved: "Reserviert", rented: "Vermietet", archived: "Archiviert", paused: "Pausiert" }[listing.status] || listing.status}</Badge>
          </div>
          <div className="text-xs text-slate-500 mt-0.5 flex gap-2 flex-wrap">
            {listing.address_public && <span>{listing.address_public}</span>}
            {listing.living_area && <span>· {listing.living_area} m²</span>}
            {listing.rooms && <span>· {listing.rooms} Zi.</span>}
            {listing.rent_cold && <span>· {Number(listing.rent_cold).toFixed(0)} € Kaltmiete</span>}
          </div>
        </div>
        <div className="text-right text-xs text-slate-500">
          <div className="flex items-center gap-1">
            <b className="text-slate-800 text-sm">{openProspects.length}</b>
            <span>offene Bewerber</span>
          </div>
          <div className="text-[10px] mt-0.5">
            {prospects.length} gesamt · Aufrufe: {listing.view_count || 0}
          </div>
          <div className="flex gap-1 justify-end mt-1.5">
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setDlg({ edit: listing.id })} data-testid={`listing-edit-${listing.id}`}>Bearbeiten</Button>
            <Button size="sm" className="h-7 text-xs bg-slate-900" onClick={() => setDlg({ work: listing })} data-testid={`listing-work-${listing.id}`}>Bewerbungen & Besichtigungen</Button>
          </div>
          {!muted && listing.status === "active" && (
            <>
              <PoolMatchButton listing={listing} />
              <CloseMarketingButton listing={listing} prospects={prospects} onReload={onReload} />
            </>
          )}
        </div>
      </div>
      {openProspects.length === 0 ? (
        <div className="px-4 py-4 text-xs text-slate-400 italic">
          Noch keine Bewerbungen für dieses Inserat.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 p-3">
          {openProspects.slice(0, 12).map((p) => (
            <ProspectCard key={p.id} prospect={p} />
          ))}
        </div>
      )}
    </div>
  );
}

function ProspectCard({ prospect: p }) {
  return (
    <Link
      to={`/vermarktung/bewerber/${p.id}`}
      className="rounded-md border border-slate-200 p-3 hover:border-emerald-400 hover:shadow-sm transition-all bg-white"
      data-testid={`prospect-card-${p.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-900 truncate">
            {p.first_name} {p.last_name}
          </div>
          <div className="text-[10px] text-slate-500 truncate mt-0.5 flex items-center gap-1">
            <Envelope size={10} /> {p.email}
          </div>
          {p.phone && (
            <div className="text-[10px] text-slate-500 truncate flex items-center gap-1">
              <PhoneIcon size={10} /> {p.phone}
            </div>
          )}
        </div>
        <span className={`inline-flex text-[9px] px-1.5 py-0.5 rounded border ${STATUS_CLS[p.status] || STATUS_CLS.new}`}>
          {STATUS_LABELS[p.status] || p.status}
        </span>
      </div>
      <div className="mt-2 pt-2 border-t border-slate-100 text-[10px] text-slate-500 flex justify-between">
        <span>{p.persons_count || 1} P. · {STAGE_LABELS[p.stage] || p.stage}</span>
        <span>{formatDate(p.applied_at)}</span>
      </div>
    </Link>
  );
}

// ============================================================================
// Detail-Seite: 6 Tabs
// ============================================================================
export function VermarktungBewerber() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [prospect, setProspect] = useState(null);
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/prospects/${id}`);
      setProspect(data);
      if (data.public_listing_id) {
        const { data: l } = await api.get(`/public-listings/${data.public_listing_id}`).catch(() => ({ data: null }));
        setListing(l);
      }
    } catch (e) {
      toast.error("Bewerbung konnte nicht geladen werden");
      navigate("/vermarktung");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const updateStatus = async (newStatus) => {
    setSaving(true);
    try {
      // Auto-Stage-Logik: gewisse Status-Änderungen implizieren Stage-Wechsel
      const patch = { status: newStatus };
      if (newStatus === "shortlist" && prospect.stage === "stage1") patch.stage = "stage2";
      if (newStatus === "offer_sent" && prospect.stage !== "stage3") patch.stage = "stage3";
      await api.patch(`/prospects/${id}`, patch);
      toast.success("Status aktualisiert");
      load();
    } catch (e) { toast.error("Fehler"); } finally { setSaving(false); }
  };

  const reject = async () => {
    if (!await window._domivusConfirm("Diese Bewerbung ablehnen? Der Bewerber erhält KEINE automatische Absage — bitte informieren Sie ihn separat.")) return;
    try { await api.post(`/prospects/${id}/reject`); toast.success("Bewerbung abgelehnt"); load(); }
    catch (e) { toast.error("Fehler"); }
  };

  if (loading) return <div className="text-center py-10 text-slate-500">Lade…</div>;
  if (!prospect) return null;

  return (
    <div className="space-y-4" data-testid="bewerber-detail">
      {/* Header */}
      <div>
        <Link to="/vermarktung" className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1">
          <ArrowLeft size={12} /> Zurück zur Übersicht
        </Link>
        <div className="mt-2 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">
              {prospect.first_name} {prospect.last_name}
            </h1>
            <div className="text-sm text-slate-500 mt-1 flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1"><Envelope size={12} /> {prospect.email}</span>
              {prospect.phone && <span className="inline-flex items-center gap-1"><PhoneIcon size={12} /> {prospect.phone}</span>}
              {listing && (
                <>
                  <span>·</span>
                  <span>Inserat: <b>{listing.title || "—"}</b></span>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-xs text-slate-500">
              <div>Beworben am {formatDate(prospect.applied_at)}</div>
              <div>Herkunft: {prospect.marketing_source} · {prospect.technical_intake}</div>
            </div>
            <Select value={prospect.status} onValueChange={updateStatus} disabled={saving}>
              <SelectTrigger className="w-56" data-testid="prospect-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_LABELS).map(([v, l]) => (
                  <SelectItem key={v} value={v}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {prospect.status !== "rejected" && (
              <Button variant="outline" onClick={reject} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="prospect-reject">
                Ablehnen
              </Button>
            )}
          </div>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <span className={`inline-flex text-[10px] px-1.5 py-0.5 rounded border ${STATUS_CLS[prospect.status] || STATUS_CLS.new}`}>
            {STATUS_LABELS[prospect.status] || prospect.status}
          </span>
          <Badge variant="outline">{STAGE_LABELS[prospect.stage] || prospect.stage}</Badge>
        </div>
      </div>

      {/* 6 Tabs */}
      <Tabs defaultValue="application">
        <TabsList className="flex-wrap">
          <TabsTrigger value="application" data-testid="tab-bewerbung"><User size={12} className="mr-1" /> Bewerbung</TabsTrigger>
          <TabsTrigger value="communication" data-testid="tab-communication"><ChatCircleDots size={12} className="mr-1" /> Kommunikation</TabsTrigger>
          <TabsTrigger value="viewings" data-testid="tab-viewings"><CalendarBlank size={12} className="mr-1" /> Besichtigungen</TabsTrigger>
          <TabsTrigger value="stage2" data-testid="tab-stage2"><ClipboardText size={12} className="mr-1" /> Erw. Angaben</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents"><Files size={12} className="mr-1" /> Unterlagen</TabsTrigger>
          <TabsTrigger value="privacy" data-testid="tab-privacy"><Shield size={12} className="mr-1" /> Datenschutz</TabsTrigger>
          <TabsTrigger value="activities" data-testid="tab-activities"><ClockCounterClockwise size={12} className="mr-1" /> Aktivitäten</TabsTrigger>
        </TabsList>

        <TabsContent value="application" className="mt-4">
          <ApplicationTab p={prospect} />
        </TabsContent>
        <TabsContent value="communication" className="mt-4">
          <CommunicationTab p={prospect} />
        </TabsContent>
        <TabsContent value="viewings" className="mt-4">
          <ViewingsTab p={prospect} />
        </TabsContent>
        <TabsContent value="stage2" className="mt-4">
          <Stage2Tab p={prospect} />
        </TabsContent>
        <TabsContent value="documents" className="mt-4">
          <DocumentsTab p={prospect} />
        </TabsContent>
        <TabsContent value="privacy" className="mt-4">
          <PrivacyTab p={prospect} onReload={load} />
        </TabsContent>
        <TabsContent value="activities" className="mt-4">
          <ActivitiesTab p={prospect} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ---- Tab: Bewerbung Stufe 1 -----------------------------------------------
function ApplicationTab({ p }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid="tab-content-bewerbung">
      <Section title="Kontakt">
        <Field label="Name">{p.first_name} {p.last_name}</Field>
        <Field label="E-Mail">{p.email}</Field>
        <Field label="Telefon">{p.phone || "—"}</Field>
        <Field label="Bevorzugter Kontakt">{p.preferred_contact || "—"}</Field>
      </Section>
      <Section title="Einzug">
        <Field label="Frühester Einzug">{p.desired_move_in ? formatDate(p.desired_move_in) : "—"}</Field>
        <Field label="Personen">{p.persons_count}</Field>
        <Field label="Nutzung">{p.usage === "main_residence" ? "Hauptwohnsitz" : (p.usage === "secondary_residence" ? "Zweitwohnsitz" : "—")}</Field>
        <Field label="Nur Wohnzwecke">{p.residential_only == null ? "—" : (p.residential_only ? "Ja" : "Nein")}</Field>
      </Section>
      <Section title="Bedarfe">
        <Field label="Tiere">{p.pets == null ? "—" : (p.pets ? "Ja" : "Nein")}</Field>
        {p.pets && p.pets_details && <Field label="Tier-Details">{p.pets_details}</Field>}
      </Section>
      <Section title="Wirtschaftliches (Grund-Angabe)">
        <Field label="Einkommensquelle">{INCOME_LABELS[p.income_source] || "—"}</Field>
        <Field label="Beschäftigungsart">{EMPL_LABELS[p.employment_type] || "—"}</Field>
        <Field label="Haushalts-Netto (Spanne)">{BRACKET_LABELS[p.income_bracket] || "—"}</Field>
      </Section>
      {p.note && (
        <Section title="Anmerkung">
          <div className="col-span-2 text-sm text-slate-700 whitespace-pre-wrap">{p.note}</div>
        </Section>
      )}
      <div className="pt-3 border-t border-slate-100 text-[10px] text-slate-400 flex items-center gap-1">
        <CheckCircle size={11} weight="fill" className="text-emerald-600" />
        DSGVO-Einwilligung erteilt am {p.consent_at ? formatDate(p.consent_at) : formatDate(p.applied_at)}
        {p.consent_ip && ` · IP: ${p.consent_ip}`}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">{title}</div>
      <div className="grid grid-cols-2 gap-2 text-sm">{children}</div>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] text-slate-400">{label}</div>
      <div className="text-slate-800">{children}</div>
    </div>
  );
}

// ---- Tab-Placeholders (Stufe 2 + Kommunikation + Besichtigung + Dokumente) ----
function CommunicationTab({ p }) {
  return <ProspectThread prospect={p} />;
}

function _LegacyCommunicationTab({ p }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-600 space-y-3" data-testid="tab-content-communication">
      <div className="flex items-center gap-2 text-slate-700 font-medium">
        <ChatCircleDots size={16} /> Kommunikation mit {p.first_name}
      </div>
      <p className="text-xs text-slate-500">
        Direkt an <b>{p.email}</b> schreiben. Alle E-Mails werden im Vorgang zu diesem Bewerber archiviert.
      </p>
      <a href={`mailto:${p.email}`} className="inline-block">
        <Button variant="outline" size="sm">
          <Envelope size={12} className="mr-1" /> E-Mail senden
        </Button>
      </a>
      <div className="pt-3 border-t border-slate-100 text-[10px] text-slate-400">
        Ausgehende Nachrichten aus dem Schreiben-Editor werden hier künftig gebündelt.
      </div>
    </div>
  );
}
function ViewingsTab({ p }) {
  const [viewings, setViewings] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    api.get("/viewings").then(({ data }) => {
      if (cancelled) return;
      // Nur Termine, in denen dieser Bewerber verlinkt ist
      setViewings((data || []).filter((v) => (v.prospect_ids || []).includes(p.id)));
    }).catch(() => setViewings([])).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [p.id]);
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid="tab-content-viewings">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-slate-700 font-medium">
          <CalendarBlank size={16} /> Besichtigungen ({viewings.length})
        </div>
        <Link to={`/besichtigungen`} className="text-xs text-emerald-700 hover:underline">
          Zum Kalender →
        </Link>
      </div>
      {loading ? (
        <div className="text-xs text-slate-500">Lade…</div>
      ) : viewings.length === 0 ? (
        <p className="text-xs text-slate-500">
          Noch keine Termine vereinbart. Legen Sie einen neuen Termin über den <Link to="/besichtigungen" className="text-emerald-700 hover:underline">Kalender</Link> an.
        </p>
      ) : (
        <ul className="space-y-2">
          {viewings.map((v) => (
            <li key={v.id} className="flex justify-between items-start border-l-2 border-emerald-300 pl-3 py-1">
              <div>
                <div className="text-sm font-medium">
                  {new Date(v.scheduled_at).toLocaleString("de-DE", { day:"2-digit", month:"2-digit", year:"numeric", hour:"2-digit", minute:"2-digit" })}
                  <Badge variant="outline" className="ml-2 text-[10px]">{v.status}</Badge>
                </div>
                <div className="text-xs text-slate-500">
                  {v.location || "—"} · {v.duration_min} Min · {(v.prospect_ids || []).length} Bewerber
                </div>
                {v.notes && <div className="text-[10px] text-slate-400 italic mt-0.5">{v.notes}</div>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
function Stage2Tab({ p }) {
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState(null);

  const issueLink = async (target) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/prospects/${p.id}/issue-magic-link`, {
        target_stage: target, ttl_days: 7,
      });
      setIssued(data);
      toast.success(`Magic-Link für ${target === "stage3" ? "Stufe 3" : "Stufe 2"} erstellt.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Link-Erstellung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const linkUrl = issued ? `${window.location.origin}${issued.public_path}` : "";
  const copyLink = () => {
    if (!linkUrl) return;
    navigator.clipboard.writeText(linkUrl).then(() => toast.success("Link kopiert."));
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-600" data-testid="tab-content-stage2">
      <div className="flex items-center gap-2 text-slate-700 font-medium mb-2">
        <ClipboardText size={16} /> Erweiterte Angaben (Stufe 2)
      </div>
      {p.stage === "stage1" ? (
        <div className="text-xs text-slate-500 space-y-3">
          <p>
            Der Bewerber befindet sich aktuell in <b>Stufe 1</b>. Über einen sicheren
            Magic-Link können Sie erweiterte Angaben (Stufe 2) oder Nachweise (Stufe 3)
            direkt vom Bewerber selbst erheben lassen — verschlüsselt, zeitlich begrenzt
            und ohne E-Mail-Anhänge.
          </p>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => issueLink("stage2")} disabled={busy}
                    data-testid="issue-stage2-btn">Stufe 2 anfordern</Button>
            <Button size="sm" variant="outline" onClick={() => issueLink("stage3")} disabled={busy}
                    data-testid="issue-stage3-btn">Direkt Stufe 3 (Nachweise)</Button>
          </div>
          {issued && (
            <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-emerald-900">
              <div className="text-xs font-semibold mb-1">✓ Link erstellt (gültig 7 Tage)</div>
              <div className="flex gap-2 items-center">
                <code className="text-[10px] bg-white border border-emerald-200 rounded px-1 py-0.5 flex-1 truncate">
                  {linkUrl}
                </code>
                <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={copyLink}
                        data-testid="copy-magic-link-btn">Kopieren</Button>
              </div>
              <p className="text-[10px] text-emerald-800 mt-1">
                Kopieren Sie den Link und senden Sie ihn dem Bewerber (E-Mail/WhatsApp).
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Field label="Bisherige Adresse">{p.current_address || "—"}</Field>
            <Field label="Arbeitgeber">{p.employer || "—"}</Field>
            <Field label="Nettoeinkommen">{p.net_income_eur ? formatEUR(p.net_income_eur) : "—"}</Field>
            <Field label="Mitbewerber">{(p.coapplicants || []).length}</Field>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => issueLink("stage2")} disabled={busy}
                    data-testid="reissue-stage2-btn">Angaben ergänzen (neuer Link)</Button>
            <Button size="sm" onClick={() => issueLink("stage3")} disabled={busy}
                    data-testid="issue-stage3-btn">Nachweise anfordern (Stufe 3)</Button>
          </div>
          {issued && (
            <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-emerald-900">
              <div className="text-xs font-semibold mb-1">✓ Link erstellt (gültig 7 Tage)</div>
              <div className="flex gap-2 items-center">
                <code className="text-[10px] bg-white border border-emerald-200 rounded px-1 py-0.5 flex-1 truncate">
                  {linkUrl}
                </code>
                <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={copyLink}
                        data-testid="copy-magic-link-btn">Kopieren</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
function DocumentsTab({ p }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-600" data-testid="tab-content-documents">
      <div className="flex items-center gap-2 text-slate-700 font-medium mb-2">
        <Files size={16} /> Unterlagen (Stufe 3)
      </div>
      {(p.stage3_documents || []).length === 0 ? (
        <p className="text-xs text-slate-500">
          Noch keine Nachweise hochgeladen. Nachweise werden vom Bewerber
          verschlüsselt via Magic-Link übermittelt (nicht per E-Mail).
        </p>
      ) : (
        <ul className="space-y-1 text-sm">
          {(p.stage3_documents || []).map((d, i) => (
            <li key={i} className="flex items-center gap-2">
              <Files size={12} className="text-slate-400" />
              <span>{d.kind || "Dokument"}</span>
              <span className="text-xs text-slate-500">· {d.uploaded_at ? formatDate(d.uploaded_at) : ""}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// 2026-02-24 · iter172 — Datenschutz-Reiter (read-only + Widerruf)
function PrivacyTab({ p, onReload }) {
  const [pv, setPv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/prospects/${p.id}/privacy`);
      setPv(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Laden");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [p.id]);

  const revoke = async () => {
    if (!confirm("Interessentenstamm-Einwilligung wirklich widerrufen?")) return;
    setSaving(true);
    try {
      await api.post(`/prospects/${p.id}/pool-consent`, { consent: false });
      toast.success("Einwilligung widerrufen");
      await load();
      onReload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally { setSaving(false); }
  };

  const grant = async () => {
    setSaving(true);
    try {
      await api.post(`/prospects/${p.id}/pool-consent`, {
        consent: true, version: "1.0",
        text: "Aufnahme in den Interessentenstamm auf Wunsch des Interessenten (manuell durch Verwaltung).",
      });
      toast.success("Einwilligung erfasst");
      await load();
      onReload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    } finally { setSaving(false); }
  };

  if (loading || !pv) return <div className="p-6 text-sm text-slate-500">Lade Datenschutz-Daten…</div>;

  const pool = pv.consents?.interessentenstamm || {};
  const dp = pv.consents?.data_processing || {};
  const ret = pv.retention || {};
  const RET_LABEL = {
    active: "Aktiv (Daten in Verarbeitung)",
    pending_deletion: "Zur Löschung vorgemerkt",
    anonymized: "Anonymisiert",
    blocked: "Gesperrt / Verarbeitung eingeschränkt",
    deleted: "Gelöscht",
  };

  return (
    <div className="space-y-4" data-testid="tab-content-privacy">
      {/* Konkrete Bewerbung */}
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-3">
          <ClipboardText size={16} /> Konkrete Bewerbung
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <PvField label="Aktenzeichen">{pv.application?.aktenzeichen || "—"}</PvField>
          <PvField label="Bewerbung">{formatDate(pv.application?.applied_at)}</PvField>
          <PvField label="Vermarktung abgeschlossen">{pv.application?.closed_at ? formatDate(pv.application.closed_at) : "—"}</PvField>
          <PvField label="Status">{STATUS_LABELS[pv.application?.status] || pv.application?.status}</PvField>
          <PvField label="Datenverarbeitungs-Einwilligung">
            {dp.granted ? "Ja" : "Nein"}{dp.at ? ` · ${formatDate(dp.at)}` : ""}{dp.ip ? ` · IP ${dp.ip}` : ""}
          </PvField>
        </div>
      </div>
      {/* Interessentenstamm */}
      <div className={`rounded-lg border p-4 ${pool.granted ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-white"}`}>
        <div className="text-sm font-semibold flex items-center justify-between mb-3">
          <span className="flex items-center gap-2 text-slate-800"><Shield size={16} /> Interessentenstamm</span>
          <div className="flex gap-2">
            {pool.granted ? (
              <Button size="sm" variant="outline" onClick={revoke} disabled={saving}
                      className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="privacy-revoke">
                <XCircle size={12} className="mr-1" /> Widerrufen
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={grant} disabled={saving}
                      className="border-emerald-300 text-emerald-800 hover:bg-emerald-50" data-testid="privacy-grant">
                <CheckCircle size={12} className="mr-1" /> Einwilligung erfassen
              </Button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <PvField label="Status">
            <b className={pool.granted ? "text-emerald-800" : "text-slate-600"}>
              {pool.granted ? "Aktiv" : (pool.status === "revoked" ? "Widerrufen" : "Nicht erteilt")}
            </b>
          </PvField>
          <PvField label="Erteilt am">{pool.at ? formatDate(pool.at) : "—"}</PvField>
          <PvField label="Version">{pool.version || "—"}</PvField>
          <PvField label="Quelle">{pool.source || "—"}</PvField>
          {pool.revoked_at && <PvField label="Widerrufen am"><b className="text-rose-700">{formatDate(pool.revoked_at)}</b></PvField>}
        </div>
        {pool.text && (
          <div className="mt-3 pt-2 border-t border-slate-200 text-[11px] text-slate-600 italic">
            „{pool.text}"
          </div>
        )}
      </div>
      {/* Löschkonzept */}
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-3">
          <ClockCounterClockwise size={16} /> Löschkonzept
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
          <PvField label="Retention-Status">
            <span className={`inline-flex text-[10px] px-1.5 py-0.5 rounded border ${
              ret.status === "pending_deletion" ? "bg-amber-50 border-amber-200 text-amber-900"
              : ret.status === "active" ? "bg-emerald-50 border-emerald-200 text-emerald-900"
              : "bg-slate-50 border-slate-200"
            }`}>{RET_LABEL[ret.status] || ret.status}</span>
          </PvField>
          <PvField label="Unterlagen (Stufe 3) löschen am">{ret.documents_delete_at ? formatDate(ret.documents_delete_at) : "—"}</PvField>
          <PvField label="IP + Consent löschen am">{ret.ip_delete_at ? formatDate(ret.ip_delete_at) : "—"}</PvField>
          <PvField label="Vollständige Löschung / Anonymisierung">{ret.full_delete_at ? formatDate(ret.full_delete_at) : (pool.granted ? "— (Stamm aktiv)" : "—")}</PvField>
        </div>
      </div>
      {/* Historie */}
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="text-sm font-semibold text-slate-800 flex items-center gap-2 mb-3">
          <ClockCounterClockwise size={16} /> Datenschutz-Historie
        </div>
        <ul className="space-y-1.5">
          {(pv.activities || []).map((a, i) => (
            <li key={i} className="text-xs flex gap-3 border-l-2 border-slate-200 pl-2 py-0.5">
              <div className="text-slate-400 tabular-nums w-32 shrink-0">{a.ts ? new Date(a.ts).toLocaleString("de-DE") : "—"}</div>
              <div>
                <b>{a.kind}</b>
                {a.meta && Object.keys(a.meta).length > 0 && (
                  <span className="text-[10px] text-slate-500 ml-2 font-mono">{JSON.stringify(a.meta)}</span>
                )}
              </div>
            </li>
          ))}
          {(pv.activities || []).length === 0 && <li className="text-xs text-slate-500">Noch keine Einträge.</li>}
        </ul>
      </div>
    </div>
  );
}

function PvField({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-slate-800">{children}</div>
    </div>
  );
}


function ActivitiesTab({ p }) {
  const acts = [...(p.activities || [])].sort((a, b) => (b.ts || "").localeCompare(a.ts || ""));
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="tab-content-activities">
      <div className="flex items-center gap-2 text-slate-700 font-medium mb-3">
        <ClockCounterClockwise size={16} /> Aktivitäten ({acts.length})
      </div>
      {acts.length === 0 ? (
        <p className="text-xs text-slate-500">Noch keine Aktivitäten erfasst.</p>
      ) : (
        <ul className="space-y-2">
          {acts.map((a, i) => (
            <li key={i} className="flex gap-3 text-sm border-l-2 border-slate-200 pl-3 py-0.5">
              <div className="text-[10px] text-slate-400 w-32 shrink-0 tabular-nums">
                {a.ts ? new Date(a.ts).toLocaleString("de-DE", { day:"2-digit", month:"2-digit", year:"numeric", hour:"2-digit", minute:"2-digit" }) : "—"}
              </div>
              <div className="flex-1">
                <div className="font-medium text-slate-700">{a.kind || "activity"}</div>
                {a.meta && Object.keys(a.meta).length > 0 && (
                  <div className="text-[10px] text-slate-500 font-mono">
                    {JSON.stringify(a.meta)}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


// 2026-02-24 · iter172 — Abschluss-Assistent (Preview + Execute)
function CloseMarketingButton({ listing, prospects, onReload }) {
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [method, setMethod] = useState("rented");
  const [editedItems, setEditedItems] = useState({}); // { prospect_id: {subject, body, skip} }

  const openProspects = (prospects || []).filter((p) => p.status !== "rejected" && p.status !== "withdrawn");

  const loadPreview = async (sid) => {
    setLoading(true);
    try {
      const _sid = (sid && sid !== "__NONE__") ? sid : null;
      const { data } = await api.post(`/public-listings/${listing.id}/close-preview`, {
        selected_prospect_id: _sid, method,
      });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler bei der Vorschau");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open && selectedId !== undefined) loadPreview(selectedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedId, method]);

  const execute = async () => {
    setExecuting(true);
    try {
      const items = (preview?.plan || [])
        .filter((it) => it.kind === "rejection")
        .map((it) => {
          const ov = editedItems[it.prospect_id] || {};
          return {
            prospect_id: it.prospect_id,
            subject: ov.subject ?? it.subject,
            body: ov.body ?? it.body,
            skip: !!ov.skip,
          };
        });
      const payload = {
        selected_prospect_id: (selectedId && selectedId !== "__NONE__") ? selectedId : null,
        method,
        items,
        send_channel: "email",
        test_recipient: testMode && testEmail ? testEmail : null,
      };
      const { data } = await api.post(`/public-listings/${listing.id}/close-execute`, payload);
      toast.success(`Vermarktung ${data.marketing?.aktenzeichen || ""} abgeschlossen · ${data.sent}/${data.total} Absagen versendet`);
      setOpen(false);
      onReload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Abschließen");
    } finally { setExecuting(false); }
  };

  const editItem = (pid, patch) => setEditedItems((s) => ({ ...s, [pid]: { ...(s[pid] || {}), ...patch } }));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="mt-1.5 gap-1 border-emerald-300 text-emerald-800 hover:bg-emerald-50" data-testid={`close-marketing-btn-${listing.id}`}>
          <Confetti size={12} /> Vermarktung abschließen
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col" data-testid="close-marketing-dialog">
        <DialogHeader>
          <DialogTitle>Vermarktung abschließen</DialogTitle>
          <DialogDescription>
            Wählen Sie den erfolgreichen Mieter. Alle anderen Interessenten erhalten
            automatisch die passende Absage. Die Nachrichten können Sie vor dem Versand
            prüfen und anpassen.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3 pb-2 border-b">
          <div>
            <label className="text-xs font-semibold text-slate-600">Ausgewählter Mieter</label>
            <Select value={selectedId} onValueChange={setSelectedId}>
              <SelectTrigger data-testid="close-select-tenant"><SelectValue placeholder="— nicht vergeben —" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__NONE__">— nicht vergeben —</SelectItem>
                {openProspects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name} · {p.email}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Abschluss-Grund</label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger data-testid="close-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="rented">Erfolgreich vermietet</SelectItem>
                <SelectItem value="cancelled">Vermarktung abgebrochen</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="text-xs">
            {preview?.marketing?.aktenzeichen && (
              <div className="rounded-md bg-slate-50 border p-2">
                <div className="text-slate-500">Aktenzeichen</div>
                <div className="font-mono font-semibold">{preview.marketing.aktenzeichen}</div>
              </div>
            )}
          </div>
        </div>
        {loading ? (
          <div className="p-6 text-center text-slate-500">Lade Preview…</div>
        ) : preview ? (
          <div className="flex-1 overflow-y-auto space-y-3">
            <div className="grid grid-cols-4 gap-2 text-xs pt-2">
              <StatMini label="Gesamt" val={preview.counts.total_prospects} />
              <StatMini label="Ausgewählt" val={preview.counts.selected} />
              <StatMini label="Absagen" val={preview.counts.rejections} tone="rose" />
              <StatMini label="Interessentenstamm aktiv" val={preview.counts.pool_active} tone="emerald" />
            </div>
            <div className="space-y-2">
              {preview.plan.map((it) => {
                if (it.kind === "selected") return (
                  <div key={it.prospect_id} className="border border-emerald-200 bg-emerald-50 rounded-md p-3">
                    <div className="text-emerald-900 font-semibold text-sm flex items-center gap-2">
                      <CheckCircle size={14} /> Ausgewählt: {it.prospect_name}
                    </div>
                    <div className="text-xs text-emerald-800 mt-0.5">{it.email} · wird nach Abschluss in Mietverhältnis überführt.</div>
                  </div>
                );
                const ov = editedItems[it.prospect_id] || {};
                const skip = !!ov.skip;
                return (
                  <div key={it.prospect_id} className={`border rounded-md p-3 ${skip ? "opacity-50 border-slate-200" : "border-slate-200"}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <div className="text-sm font-semibold">{it.prospect_name}</div>
                        <div className="text-[11px] text-slate-500">{it.email} · Vorlage: <b>{it.template_title}</b></div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <Badge variant="outline" className="text-[10px]">{STATUS_LABELS[it.current_status] || it.current_status}</Badge>
                        {it.pool_consent && <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-800">Interessentenstamm aktiv</Badge>}
                        <label className="text-[10px] flex items-center gap-1 cursor-pointer">
                          <Checkbox checked={skip} onCheckedChange={(v) => editItem(it.prospect_id, { skip: !!v })} data-testid={`close-skip-${it.prospect_id}`} />
                          Überspringen
                        </label>
                      </div>
                    </div>
                    <Input value={ov.subject ?? it.subject} onChange={(e) => editItem(it.prospect_id, { subject: e.target.value })}
                           placeholder="Betreff" className="mb-2 text-xs" data-testid={`close-subject-${it.prospect_id}`} />
                    <Textarea value={ov.body ?? it.body} onChange={(e) => editItem(it.prospect_id, { body: e.target.value })}
                              rows={5} className="text-xs font-mono" data-testid={`close-body-${it.prospect_id}`} />
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
        <div className="border-t pt-2 space-y-2">
          <label className="text-xs flex items-center gap-2">
            <Checkbox checked={testMode} onCheckedChange={setTestMode} data-testid="close-test-mode" />
            Testversand an eine Adresse
          </label>
          {testMode && <Input type="email" placeholder="test@example.com" value={testEmail}
                              onChange={(e) => setTestEmail(e.target.value)} data-testid="close-test-email" />}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
          <Button onClick={execute} disabled={executing || !preview}
                  className="bg-emerald-700 hover:bg-emerald-800 gap-1.5" data-testid="close-marketing-execute">
            <Confetti size={14} /> {executing ? "Wird abgeschlossen…" : `Abschluss ausführen (${preview?.counts?.rejections || 0} Absagen)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatMini({ label, val, tone }) {
  const cls = tone === "rose" ? "bg-rose-50 border-rose-200 text-rose-900"
    : tone === "emerald" ? "bg-emerald-50 border-emerald-200 text-emerald-900"
    : "bg-slate-50 border-slate-200 text-slate-900";
  return (
    <div className={`rounded-md border p-2 text-center ${cls}`}>
      <div className="text-lg font-bold tabular-nums">{val}</div>
      <div className="text-[10px] uppercase tracking-wider">{label}</div>
    </div>
  );
}



// 2026-02-24 · iter173 — Match-Vorschläge aus dem Interessentenstamm
function PoolMatchButton({ listing }) {
  const [open, setOpen] = useState(false);
  const [matches, setMatches] = useState(null);
  const [count, setCount] = useState(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [selected, setSelected] = useState({});
  const [sending, setSending] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  // Count-Only auf Card-Mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post(`/prospect-pool/matches`, { listing_id: listing.id });
        if (!cancelled) setCount(data.count || 0);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [listing.id]);

  const loadDetails = async () => {
    try {
      const { data } = await api.post(`/prospect-pool/matches`, { listing_id: listing.id });
      setMatches(data.matches || []);
      const initSel = {};
      for (const m of data.matches || []) initSel[m.prospect_id] = true;
      setSelected(initSel);
      // Default-Nachricht
      setSubject(`Neues Angebot: ${listing.title || "Wohnung"}`);
      setBody(
        `<p>Guten Tag,</p>` +
        `<p>passend zu Ihrem hinterlegten Suchprofil im Interessentenstamm möchten wir Ihnen ein neues Angebot vorstellen:</p>` +
        `<ul>` +
        `<li><b>${listing.title || "Wohnung"}</b></li>` +
        (listing.address_public_city ? `<li>Ort: ${listing.address_public_city}</li>` : "") +
        (listing.rooms ? `<li>Zimmer: ${listing.rooms}</li>` : "") +
        (listing.rent_cold ? `<li>Kaltmiete: ${Number(listing.rent_cold).toFixed(0)} €</li>` : "") +
        `</ul>` +
        `<p>Wenn das für Sie interessant klingt, melden Sie sich gerne direkt bei uns.</p>` +
        `<p>Mit freundlichen Grüßen<br/>Ihre Hausverwaltung</p>`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Laden der Matches");
    }
  };

  useEffect(() => { if (open) loadDetails(); /* eslint-disable-next-line */ }, [open]);

  const send = async () => {
    const pids = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
    if (!pids.length) { toast.error("Bitte mindestens einen Interessenten auswählen"); return; }
    setSending(true);
    try {
      const payload = {
        listing_id: listing.id,
        subject, body_html: body,
        prospect_ids: pids,
        include_unsubscribe_link: true,
        test_recipient: testMode && testEmail ? testEmail : null,
      };
      const { data } = await api.post(`/prospect-pool/notify`, payload);
      toast.success(`${data.sent} von ${data.total} Nachrichten versendet`);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Versand");
    } finally { setSending(false); }
  };

  if (count === null || count === 0) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="mt-1.5 gap-1 border-blue-300 text-blue-800 hover:bg-blue-50" data-testid={`pool-match-btn-${listing.id}`}>
          <Shield size={12} /> {count} Interessent{count === 1 ? "" : "en"} aus Stamm
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col" data-testid={`pool-match-dialog-${listing.id}`}>
        <DialogHeader>
          <DialogTitle>Passende Interessenten informieren</DialogTitle>
          <DialogDescription>
            {count} aktive Kontakte im Interessentenstamm entsprechen den Eckdaten dieses Inserats.
            Alle Nachrichten enthalten automatisch einen DSGVO-konformen Widerrufslink.
          </DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600">Betreff</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="pool-notify-subject" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Nachricht (HTML)</label>
            <Textarea rows={10} value={body} onChange={(e) => setBody(e.target.value)}
                      className="font-mono text-xs" data-testid="pool-notify-body" />
          </div>
          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50">
                <tr>
                  <th className="w-8 px-2 py-1.5"></th>
                  <th className="text-left px-2 py-1.5">Name</th>
                  <th className="text-left px-2 py-1.5">E-Mail</th>
                  <th className="text-left px-2 py-1.5">Suchprofil</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(matches || []).map((m) => (
                  <tr key={m.prospect_id}>
                    <td className="px-2 py-1.5">
                      <input type="checkbox" checked={!!selected[m.prospect_id]}
                             onChange={(e) => setSelected((s) => ({ ...s, [m.prospect_id]: e.target.checked }))}
                             data-testid={`pool-sel-${m.prospect_id}`} />
                    </td>
                    <td className="px-2 py-1.5 font-medium">{m.name}</td>
                    <td className="px-2 py-1.5 text-slate-600">{m.email}</td>
                    <td className="px-2 py-1.5 text-[10px] text-slate-500">
                      {(m.search_profile?.regions || []).join(", ")}
                      {m.search_profile?.max_rent_cold ? ` · ≤ ${m.search_profile.max_rent_cold} € kalt` : ""}
                      {m.search_profile?.min_rooms ? ` · ${m.search_profile.min_rooms}+ Zi.` : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pt-2 border-t">
            <label className="text-xs flex items-center gap-2">
              <input type="checkbox" checked={testMode} onChange={(e) => setTestMode(e.target.checked)} data-testid="pool-test-mode" />
              Testversand an eine Adresse
            </label>
            {testMode && <Input type="email" placeholder="test@example.com" value={testEmail}
                                 onChange={(e) => setTestEmail(e.target.value)} data-testid="pool-test-email" className="mt-1" />}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
          <Button onClick={send} disabled={sending || !subject} className="bg-blue-700 hover:bg-blue-800 gap-1.5" data-testid="pool-notify-send">
            <EnvelopeSimple size={14} /> {sending ? "Versende…" : "Sammel-Mail senden"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

